import { onMounted, onUnmounted, ref } from 'vue';
import { point } from '@/utils';

/**
 * 页面视图打点 Hook
 * 使用 IntersectionObserver 监听组件是否进入视口，自动上报 view_page 事件
 * @param eventType - 页面类型，如 '首页'、'里程碑'、'积分抽奖'、'版本内容页'
 * @param options - IntersectionObserver 配置项
 */
export default function useViewPagePoint(
  eventType: string,
  options: IntersectionObserverInit = {}
) {
  const targetRef = ref<HTMLElement | null>(null);
  let observer: IntersectionObserver | null = null;

  onMounted(() => {
    // 等待页面完全加载后再初始化 observer
    const initObserver = () => {
      if (!targetRef.value) return;

      // 默认配置：当元素至少 50% 可见时触发
      const defaultOptions: IntersectionObserverInit = {
        threshold: 0.5,
        ...options,
      };

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          // 每次元素进入视口都上报
          if (entry.isIntersecting) {
            point({
              eventName: 'view_page',
              eventType,
            });
          }
        });
      }, defaultOptions);

      observer.observe(targetRef.value);
    };

    // 使用 requestIdleCallback 或 setTimeout 确保页面加载完成
    if (typeof window !== 'undefined') {
      if ('requestIdleCallback' in window) {
        // 优先使用 requestIdleCallback，在浏览器空闲时初始化
        window.requestIdleCallback(() => {
          initObserver();
        });
      } else {
        // 降级方案：等待 1 秒后初始化
        setTimeout(() => {
          initObserver();
        }, 1000);
      }
    }
  });

  onUnmounted(() => {
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  });

  return {
    targetRef,
  };
}
